import { createStreamFromBackboneModel } from 'streams/utils/streamUtils';
import { currentUser as currentUserModel } from 'models/currentUser';
import { filter, BehaviorSubject } from 'rxjs';
import {
  APICurrentUser,
  APICurrentUserFavorites,
  AssetType,
  Organization,
  PersonalSetting,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { setLocaleFromUserSettings } from '../../utils/localeUtils';

export type CurrentUserOrganization = Omit<
  Organization,
  'is-trial' | 'days-remaining-trial' | 'expired'
>;

/**
 * @deprecated Use APICurrentUser directly
 */
export type CurrentUserState = APICurrentUser;

export const getFavorites = ({
  settings,
}: APICurrentUser): APICurrentUserFavorites => ({
  dashboards: [],
  [AssetType.FOLDER]: [],
  [AssetType.WORKSPACE]: [],
  [AssetType.METAMODEL]: [],
  [AssetType.PRESENTATION]: [],
  [AssetType.SCENARIO]: [],
  [AssetType.SURVEY]: [],
  [AssetType.REPORT]: [],
  [AssetType.DASHBOARD]: [],
  [AssetType.VIEWPOINT]: [],
  [AssetType.BROADCAST]: [],
  [AssetType.TRAVERSAL]: [],
  [AssetType.BOOKMARK]: [],
  ...settings[PersonalSetting.FAVORITE],
});

/**
 * Due how Backbone models works and how we transform it this stream will emit
 * APICurrentUser or an object with all the attributes set to undefined.
 * When checking if (user) or isTruthy(user) this of course passes, but is
 * incorrect and leads to issues in some eager code (persistent streams) that
 * run before `bootstrapApplication` which loads the current user finishes
 * running.
 */
const dishonestCurrentUser$ =
  createStreamFromBackboneModel<APICurrentUser>(currentUserModel);

/**
 * There are two cases where currentUser may be null:
 * 1) Before the user is loaded during application bootstrap
 * 2) Public presentations, where the user is not authenticated
 *
 * By using BehaviorSubject we almost eliminate 1) because we can set the
 * current user value synchronously during bootstrapping and make it instantly
 * available to all subscribers and side-effecting reads before initializing
 * the rest of the application. This enables us to transition away from using
 * Backbone to manage current user via the currentUserInterface at first, and
 * gradually migrate fully to rxjs over time.
 *
 * Regarding 2) Public presentations, currentUser can be null and cause
 * problems in streams like loadedCurrentUser$ which never emits if we never
 * have a current user value. Future iterations should be look into creating a
 * fake "public user" in order to make the use-cases symmetric and avoid
 * foot-guns.
 *
 * Do NOT use this directly in application code, it is for system-level usage.
 * Preferably use `currentUser$` or if you cannot then `currentUserInterface`.
 */
const userSubject = new BehaviorSubject<APICurrentUser | null>(null);

/**
 * Used to set the current user value inside the user subject, only needed
 * for initialization or syncing with the CurrentUser Backbone model.
 */
export const internalUseOnlyNextCurrentUser = (user: APICurrentUser) => {
  userSubject.next(user);
};

/**
 * Only needed for resetting the state between tests
 */
export const internalUserOnlyResetCurrentUser = () => {
  userSubject.next(null);
};

/**
 * For usage in currentUserInterface, do not use directly.
 * Returns the current value synchronously from the user subject.
 */
export const internalUseOnlyGetCurrentUser = (): APICurrentUser | null => {
  return userSubject.getValue();
};

// Setup listeners to keep user data in sync
currentUserModel.on('sync', () => {
  // In public presentations currentUserModel calls sync with invalid user
  if (currentUserModel.attributes._id === undefined) {
    return;
  }

  internalUseOnlyNextCurrentUser(structuredClone(currentUserModel.attributes));
  setLocaleFromUserSettings(structuredClone(currentUserModel.attributes));
});

/**
 * Current user with honest types.
 * When used in a public presentation currentUser$ may emit null
 */
export const currentUser$ = userSubject.asObservable();

/**
 * For an authenticated user the organization always arrives atomically in the
 * same `/user/current_user` payload as their identity (org switching is a full
 * reload, never an in-place patch), so a loaded user always has one. The sole
 * org-less case is the anonymous public-presentation stub — a truthy user whose
 * `organization` is null, which is not a real user. Filtering it here keeps
 * `loadedCurrentUser$`'s `APICurrentUser` contract honest (organization is
 * non-null) and avoids null dereferences downstream (e.g.
 * `currentOrganizationId$`).
 */
const isLoadedCurrentUser = (
  user: APICurrentUser | null
): user is APICurrentUser => isTruthy(user) && isTruthy(user.organization);

/**
 * The current user model singleton is empty until loaded. This stream only
 * emits when the user is done loading, and should NOT be used in relation
 * to presentations because it may "freeze" observables because it never emits.
 */
export const loadedCurrentUser$ = currentUser$.pipe(
  filter(isLoadedCurrentUser)
);

export { dishonestCurrentUser$ };
